import axios from 'axios';
import { config } from '../config/environment';
import {
  ApiResponse,
  ScriptsData,
  ScriptItem,
  ScriptStructure,
  LeadsResponse,
  DashboardStats,
  VendasResponse,
  InteracoesResponse,
  BotMessageRequest,
  BotValidationRequest,
  BotValidationResponse,
  BotConfig,
  MediaResponse,
  MediaConfigResponse,
  MediaConfigInfo,
  MediaUpdateRequest,
  MediaUploadRequest,
  MediaSearchRequest,
  MediaSearchResponse,
  TypingDelayResponse,
  TypingSimulation,
  WhatsAppMessageRequest,
  WhatsAppMediaRequest,
  CreateLeadRequest,
  UpdateLeadRequest,
  CreateVendaRequest,
  CreateInteracaoRequest,
  Lead,
  Venda,
  Interacao,
  MediaInfo,
  GmailLabelsResponse,
  GmailMessagesResponse,
  GmailMessage,
  GmailSendMessageRequest,
  GmailSendMessageResponse,
  GmailTokenConfigStatus,
  GmailOAuthUrlResponse,
  GmailOAuthCallbackResponse
} from '../types/api';
import type { AIGenerationResponse } from '../types/aiGeneration';
import type {
  BraipConfigStatus,
  BraipConfigRequest,
  BraipWebhookHistoryResponse,
  BraipStats,
  BraipTestTokenRequest,
  BraipTestTokenResponse,
  BraipHealthResponse,
  BraipProductsResponse
} from '../types/braip';

export const subscriptionApi = {
  /**
   * NOVO: Cria assinatura PENDING e retorna checkoutUrl Cakto.
   * Em modo dev, cria assinatura ACTIVE diretamente.
   */
  create: (googleId: string, planType: 'mensal' | 'trimestral' | 'anual', userEmail?: string, userName?: string) => {
    const isDevMode = config.isDevelopment;
    console.log('[DEBUG] subscriptionApi.create - Criando assinatura Cakto para:', googleId, 'plano:', planType, 'devMode:', isDevMode);

    const payload: any = { planType };
    if (userEmail) payload.userEmail = userEmail;
    if (userName) payload.userName = userName;
    if (googleId) payload.googleId = googleId;
    if (isDevMode) payload.devMode = true;

    const headers: any = {};
    if (googleId) headers['X-User-ID'] = googleId;
    if (isDevMode) headers['X-Dev-Mode'] = 'true';

    return api.post('/api/subscriptions/create', payload, { headers }).then((res: any) => {
      console.log('[DEBUG] ✅ Resposta do backend (Cakto):', res.data);
      return res.data;
    });
  },

  /**
   * DEPRECATED: Usar subscriptionApi.create()
   */
  subscribe: (googleId: string, userEmail?: string, userName?: string) => {
    console.log('[WARN] subscriptionApi.subscribe é DEPRECATED, use subscriptionApi.create()');
    console.log('[DEBUG] subscriptionApi.subscribe - Criando assinatura para:', googleId);
    const payload: any = { googleId };
    if (userEmail) payload.userEmail = userEmail;
    if (userName) payload.userName = userName;
    return api.post('/api/subscribe', payload).then((res: any) => {
      console.log('[DEBUG] ✅ Resposta do backend:', res.data);
      console.log('[DEBUG] ✅ Token recebido:', res.data.token);
      return res.data;
    });
  },

  getStatus: (googleId: string) => {
    return api.get('/api/user/status', { params: { googleId } }).then((res: any) => res.data);
  },
  hasSubscription: (googleId: string) => {
    console.log('[DEBUG] subscriptionApi.hasSubscription - Verificando assinatura para:', googleId);
    return api.get('/api/user/hasSubscription', { params: { googleId } }).then((res: any) => {
      console.log('[DEBUG] ✅ Status do backend:', res.data);
      return res.data;
    });
  },
  getDetails: (googleId: string) => {
    console.log('[DEBUG] subscriptionApi.getDetails - Buscando detalhes para:', googleId);
    return api.get('/api/subscription/details', { params: { googleId } }).then((res: any) => {
      console.log('[DEBUG] ✅ Detalhes do backend:', res.data);
      return res.data;
    });
  },
  listSubscriptions: () => {
    return api.get('/api/subscriptions/list').then((res: any) => res.data);
  },
  unsubscribe: (googleId: string) => {
    console.log('[DEBUG] subscriptionApi.unsubscribe - Removendo assinatura para:', googleId);
    return api.post('/api/unsubscribe', { googleId }).then((res: any) => {
      console.log('[DEBUG] ✅ Resposta de remoção:', res.data);
      return res.data;
    });
  },
};

export const emailApi = {
  sendEnterpriseWelcome: (to: string, name: string) =>
    api.post('/email/enterprise-welcome', { to, name }).then(res => res.data),
};

// Configuração base do Axios
export const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000, // 30s para requisições normais
  headers: {
    'Content-Type': 'application/json',
  },
});

// Instância separada para chamadas de IA (timeout maior)
const aiApi = axios.create({
  baseURL: config.apiUrl,
  timeout: 120000, // 120s (2 minutos) para IA gerativa
  headers: {
    'Content-Type': 'application/json',
  },
});

const buildUserHeaders = (userId?: string) => {
  if (!userId || userId.trim() === '') {
    console.warn('[buildUserHeaders] ⚠️ userId vazio ou inválido:', userId);
    return undefined;
  }
  console.log('[buildUserHeaders] ✅ Criando header X-User-ID:', userId);
  return { 'X-User-ID': userId };
};

// Interceptors para tratamento de erros (API normal)
api.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Interceptors para tratamento de erros (AI API com timeout maior)
aiApi.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    console.error('AI API Error:', error);
    if (error.code === 'ECONNABORTED') {
      console.error('⏱️ Timeout na chamada de IA - A API Claude demorou mais que 120s');
    }
    return Promise.reject(error);
  }
);

// Scripts API
export const scriptsApi = {
  getAll: (userId?: string): Promise<ApiResponse<ScriptsData>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/v1/scripts', { headers }).then((res: any) => res.data);
  },

  getScript: (tipo: string, nome: string, userId?: string): Promise<ApiResponse<ScriptItem>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get(`/api/v1/scripts/${tipo}/${nome}`, { headers }).then((res: any) => res.data);
  },

  updateScript: (tipo: string, nome: string, conteudo: string, userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    const data = { conteudo };
    console.log('🔧 API updateScript chamada:', { tipo, nome, userId, hasHeader: !!userId, contentLength: conteudo.length });
    return api.put(`/api/v1/scripts/${tipo}/${nome}`, data, { headers }).then((res: any) => {
      console.log('✅ API updateScript resposta:', res.data);
      return res.data;
    });
  },

  updateFatosProduto: (conteudo: string, userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    const data = { conteudo };
    console.log('🔧 API updateFatosProduto chamada:', { userId, hasHeader: !!userId, contentLength: conteudo.length });
    return api.put('/api/v1/scripts/fatos_produto', data, { headers }).then((res: any) => {
      console.log('✅ API updateFatosProduto resposta:', res.data);
      return res.data;
    });
  },

  resetScript: (tipo: string, nome: string, userId: string): Promise<ApiResponse> => {
    const headers = { 'X-User-ID': userId };
    return api.delete(`/api/v1/scripts/${tipo}/${nome}`, { headers }).then((res: any) => res.data);
  },

  getUserCustomizations: (userId: string): Promise<ApiResponse> => {
    const headers = { 'X-User-ID': userId };
    return api.get('/api/v1/scripts/user-customizations', { headers }).then((res: any) => res.data);
  },

  getSystemStats: (): Promise<ApiResponse> =>
    api.get('/api/v1/scripts/stats').then((res: any) => res.data),

  reloadScripts: (): Promise<ApiResponse> =>
    api.post('/api/v1/scripts/reload').then((res: any) => res.data),

  resetScripts: (userId: string): Promise<ApiResponse> => {
    console.log('[scriptsApi.resetScripts] 🔄 Resetando scripts para userId:', userId);
    const headers = buildUserHeaders(userId);
    console.log('[scriptsApi.resetScripts] Headers:', headers);
    
    return api.post('/api/v1/scripts/reset', null, { headers })
      .then((res: any) => {
        console.log('[scriptsApi.resetScripts] ✅ Sucesso:', res.data);
        return res.data;
      })
      .catch((error: any) => {
        console.error('[scriptsApi.resetScripts] ❌ Erro:', error.response?.status, error.response?.data);
        throw error;
      });
  },

  applyGeneratedScripts: (scripts: AIGenerationResponse['scripts'], userId: string): Promise<ApiResponse> => {
    const headers = buildUserHeaders(userId);

    const payload = {
      scripts: scripts.map((script) => {
        // Normaliza conteúdo: remove linhas vazias e faz trim
        const normalizedContent = typeof script.content === 'string'
          ? script.content.split(/\r?\n/).map(line => line.trim()).filter(Boolean).join('\n')
          : script.content;

        return {
          type: script.type,
          name: script.name,
          content: normalizedContent,
          reasoning: script.reasoning,
        };
      })
    };

    return api
      .post<ApiResponse>('/api/v1/scripts/apply-generated', payload, { headers })
      .then((res) => res.data);
  },

  generateWithAI: (
    requestData: string | { prompt: string; existingScripts?: any[] }, 
    userId?: string
  ): Promise<ApiResponse<AIGenerationResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    
    // Suportar tanto string (retrocompatibilidade) quanto objeto (para atualizações)
    const data = typeof requestData === 'string' ? { prompt: requestData } : requestData;
    const isUpdate = typeof requestData === 'object' && (requestData.existingScripts?.length ?? 0) > 0;
    
    console.log('🤖 API generateWithAI chamada:', { 
      userId, 
      hasHeader: !!userId, 
      promptLength: data.prompt.length,
      isUpdate,
      existingScriptsCount: data.existingScripts?.length || 0
    });
    
    // Usa aiApi com timeout de 120s para chamadas de IA gerativa
    return aiApi.post<ApiResponse<AIGenerationResponse>>('/api/v1/scripts/generate-ai', data, { headers }).then(res => {
      console.log('✅ API generateWithAI resposta:', res.data);
      return res.data;
    });
  },

  getStructure: (): Promise<ApiResponse<ScriptStructure>> =>
    api.get('/api/v1/scripts/estrutura').then((res: any) => res.data),
};

// Leads API
export const leadsApi = {
  getAll: (): Promise<ApiResponse<LeadsResponse>> =>
    api.get('/api/v1/leads').then((res: any) => res.data),
  
  getDashboard: (): Promise<ApiResponse<DashboardStats>> =>
    api.get('/api/v1/leads/dashboard').then((res: any) => res.data),
  
  create: (lead: CreateLeadRequest): Promise<ApiResponse<Lead>> =>
    api.post('/api/v1/leads', lead).then((res: any) => res.data),
  
  update: (id: number, lead: UpdateLeadRequest): Promise<ApiResponse> =>
    api.put(`/v1/leads/${id}`, lead).then((res: any) => res.data),
  
  delete: (id: number): Promise<ApiResponse> =>
    api.delete(`/v1/leads/${id}`).then((res: any) => res.data),
  
  getByStatus: (status: string): Promise<ApiResponse<LeadsResponse>> =>
    api.get(`/v1/leads/status/${status}`).then((res: any) => res.data),
  
  getByPhone: (telefone: string): Promise<ApiResponse<Lead>> =>
    api.get(`/v1/leads/telefone/${telefone}`).then((res: any) => res.data),
  
  getFollowUp: (): Promise<ApiResponse<LeadsResponse>> =>
    api.get('/api/v1/leads/followup').then((res: any) => res.data),
  
  getStats: (): Promise<ApiResponse<DashboardStats>> =>
    api.get('/api/v1/leads/estatisticas').then((res: any) => res.data),
    
  // Novo endpoint para leads em tempo real
  getLeadsNovosHoje: (): Promise<ApiResponse<{ count: number }>> =>
    api.get('/api/v1/leads/novos-hoje').then((res: any) => res.data),
};

// Data API (Vendas e Interações)
export const dataApi = {
  deleteVenda: (id: number): Promise<ApiResponse> =>
    api.delete(`/api/v1/data/vendas/${id}`).then((res: any) => res.data),
  getVendas: (): Promise<ApiResponse<VendasResponse>> =>
    api.get('/api/v1/data/vendas').then((res: any) => res.data),
  
  getTotalVendas: (): Promise<ApiResponse<{ total: number; count: number }>> =>
    api.get('/api/v1/data/vendas/total').then((res: any) => res.data),
  
  createVenda: (venda: CreateVendaRequest): Promise<ApiResponse<Venda>> =>
    api.post('/api/v1/data/vendas', venda).then((res: any) => res.data),
  // Removido updateVenda duplicado
  updateVenda: (id: number, venda: CreateVendaRequest): Promise<ApiResponse<Venda>> =>
    api.put(`/api/v1/data/vendas/${id}`, venda).then((res: any) => res.data),
  // Novo método: pega stacks já agrupados do backend
  getInteracoesRecentesStacked: (): Promise<ApiResponse<InteracoesResponse>> =>
    api.get('/api/v1/data/interacoes/recentes').then((res: any) => res.data),
  
  createInteracao: (interacao: CreateInteracaoRequest): Promise<ApiResponse> =>
    api.post('/api/v1/data/interacoes', interacao).then((res: any) => res.data),
  
  getStats: (): Promise<ApiResponse<DashboardStats>> =>
    api.get('/api/v1/data/stats').then((res: any) => res.data),
    
  // Novos endpoints para dados em tempo real
  getInteracoesBotUltimas24h: (): Promise<ApiResponse<{ count: number }>> =>
    api.get('/api/v1/data/interacoes/bot24h').then((res: any) => res.data),

  getVendasHoje: (): Promise<ApiResponse<{ count: number }>> =>
    api.get('/api/v1/data/vendas/hoje').then((res: any) => res.data),
};

// Bot API
export const botApi = {
  sendMessage: (request: BotMessageRequest): Promise<ApiResponse<string>> =>
    api.post('/api/v1/bot/message', request).then((res: any) => res.data),
  
  validateMessage: (request: BotValidationRequest): Promise<ApiResponse<BotValidationResponse>> =>
    api.post('/api/v1/bot/validate', request).then((res: any) => res.data),
  
  getConfig: (): Promise<ApiResponse<BotConfig>> =>
    api.get('/api/v1/bot/config').then((res: any) => res.data),
  
  getUserStats: (userId: string): Promise<ApiResponse<string>> =>
    api.get(`/v1/bot/stats/${userId}`).then((res: any) => res.data),
  
  clearContext: (userId: string): Promise<ApiResponse> =>
    api.post(`/v1/bot/context/clear/${userId}`).then((res: any) => res.data),
  
  // Obtém intenções/categorias do serviço de intents
  getIntents: (): Promise<ApiResponse<{ intents: string[] }>> =>
    api.get('/api/v1/bot/intents').then((res: any) => res.data),
};

// AI Config API
export const aiConfigApi = {
  get: (): Promise<ApiResponse<{ openaiKey?: string; openrouterKey?: string }>> =>
    api.get('/api/v1/ai-config').then((res: any) => res.data),
  save: (payload: { openaiKey?: string; openrouterKey?: string }): Promise<ApiResponse<{ [k: string]: any }>> =>
    api.post('/api/v1/ai-config', payload).then((res: any) => res.data),
  validate: (payload: { openaiKey?: string; openrouterKey?: string }): Promise<ApiResponse<{ openaiValid: boolean; openrouterValid: boolean }>> =>
    api.post('/api/v1/ai-config/validate', payload).then((res: any) => res.data),
};

// Media API
export const mediaApi = {
  // Obter todas as configurações de mídia
  getAll: (userId?: string): Promise<ApiResponse<MediaConfigResponse>> =>
    api.get('/api/v1/media', {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data),

  // Obter configuração específica de uma mídia
  getConfig: (
    filename: string,
    userId?: string
  ): Promise<ApiResponse<{ filename: string; config: MediaConfigInfo }>> =>
    api.get(`/api/v1/media/config/${filename}`, {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data),

  // Atualizar configuração de uma mídia
  updateConfig: (filename: string, config: MediaUpdateRequest): Promise<ApiResponse> => {
    const { userId, ...payload } = config;
    return api.put(`/api/v1/media/config/${filename}`, payload, {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data);
  },

  // Remover configuração de uma mídia
  removeConfig: (filename: string, userId?: string): Promise<ApiResponse> =>
    api.delete(`/api/v1/media/config/${filename}`, {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data),

  // Upload de nova mídia
  upload: (request: MediaUploadRequest): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('palavras_chave', request.palavras_chave);
    if (request.descricao) formData.append('descricao', request.descricao);
    if (request.delay_segundos) formData.append('delay_segundos', request.delay_segundos.toString());
    if (request.userId) formData.append('userId', request.userId);

    return api.post('/api/v1/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(buildUserHeaders(request.userId) ?? {}),
      }
    }).then((res: any) => res.data);
  },

  // Recarregar configurações
  reload: (userId?: string): Promise<ApiResponse> =>
    api.post('/api/v1/media/reload', undefined, {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data),

  // Obter estrutura e mapeamento de contextos
  getStructure: (
    userId?: string
  ): Promise<ApiResponse<{ intencoes_palavras_chave: { [key: string]: string[] }; tipos_suportados: string[]; diretorios: { [key: string]: string } }>> =>
    api.get('/api/v1/media/estrutura', {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data),

  // Buscar mídias por mensagem e contexto
  search: (request: MediaSearchRequest, userId?: string): Promise<ApiResponse<MediaSearchResponse>> =>
    api.post('/api/v1/media/buscar', request, {
      headers: buildUserHeaders(userId),
    }).then((res: any) => res.data),
};

// Typing Profiles API
export const typingApi = {
  calculateDelay: (userId: string, message: string): Promise<ApiResponse<TypingDelayResponse>> =>
    api.post('/api/v1/typing-profiles/calculate-delay', { userId, message }).then((res: any) => res.data),
  
  updateProfile: (userId: string, message: string, responseTime: number): Promise<ApiResponse> =>
    api.post('/api/v1/typing-profiles/update-profile', { userId, message, responseTime }).then((res: any) => res.data),
  
  getUserProfile: (userId: string): Promise<ApiResponse<{ userId: string; stats: string }>> =>
    api.get(`/v1/typing-profiles/user/${userId}`).then((res: any) => res.data),
  
  resetUserProfile: (userId: string): Promise<ApiResponse> =>
    api.delete(`/v1/typing-profiles/user/${userId}`).then((res: any) => res.data),
  
  getStats: (): Promise<ApiResponse<{ [key: string]: any }>> =>
    api.get('/api/v1/typing-profiles/estatisticas').then((res: any) => res.data),
  
  getAllProfiles: (): Promise<ApiResponse<{ totalProfiles: number; profiles: { [key: string]: any } }>> =>
    api.get('/api/v1/typing-profiles/all-profiles').then((res: any) => res.data),
  
  simulateTyping: (userId: string, message: string, iterations: number): Promise<ApiResponse<TypingSimulation>> =>
    api.post('/api/v1/typing-profiles/simulate-typing', { userId, message, iterations }).then((res: any) => res.data),
  
  healthCheck: (): Promise<ApiResponse<{ [key: string]: any }>> =>
    api.get('/api/v1/typing-profiles/health').then((res: any) => res.data),
};

// WhatsApp API
export const whatsappApi = {
  sendMessage: (request: WhatsAppMessageRequest): Promise<ApiResponse> =>
    api.post('/api/v1/whatsapp/send/message', request).then((res: any) => res.data),
  
  sendInstantMessage: (request: WhatsAppMessageRequest): Promise<ApiResponse> =>
    api.post('/api/v1/whatsapp/send/instant', request).then((res: any) => res.data),
  
  markAsRead: (messageId: string, userId?: string): Promise<ApiResponse> =>
    api.post('/api/v1/whatsapp/send/mark-read', { messageId, userId }).then((res: any) => res.data),
  
  activateConversation: (userId: string, messageId?: string): Promise<ApiResponse> =>
    api.post('/api/v1/whatsapp/send/activate', { userId, messageId }).then((res: any) => res.data),
  
  sendMedia: (request: WhatsAppMediaRequest): Promise<ApiResponse> =>
    api.post('/api/v1/whatsapp/send/media', request).then((res: any) => res.data),
};

// Linguagem/Gírias API
export const linguagemApi = {
  setGlobalSlang: (ativado: boolean): Promise<ApiResponse> =>
    api.post('/api/linguagem/girias/global', { ativado }).then((res: any) => res.data),

  setUserSlang: (userId: string, ativado: boolean): Promise<ApiResponse> =>
    api.post(`/api/linguagem/girias/usuario/${userId}`, { ativado }).then((res: any) => res.data),

  getSlangStatus: (userId?: string): Promise<ApiResponse<{ girias_global_ativado: boolean; girias_usuario_ativado?: boolean }>> =>
    api.get('/api/linguagem/girias/status', { params: userId ? { userId } : {} }).then((res: any) => res.data),

  getStatistics: (): Promise<ApiResponse> =>
    api.get('/api/linguagem/estatisticas').then((res: any) => res.data),

  testHumanization: (texto: string, userId?: string, usarGirias?: boolean): Promise<ApiResponse> =>
    api.post('/api/linguagem/testar', { texto, userId, usarGirias }).then((res: any) => res.data),
};

// Config API (Persona, Produto, Emojis)
export const configApi = {
  /**
   * Sincroniza automaticamente configs extraídas dos scripts gerados pela IA
   * Chamado após aprovar scripts no ScriptGenerator
   */
  syncFromScripts: (userId: string, scripts: AIGenerationResponse['scripts']): Promise<ApiResponse> => {
    console.log('🔄 configApi.syncFromScripts - Sincronizando configs para userId:', userId);
    console.log('📦 Scripts a sincronizar:', scripts.length);

    const payload = { scripts };

    return api.post(`/api/config/sync-from-scripts/${userId}`, payload)
      .then((res: any) => {
        console.log('✅ configApi.syncFromScripts - Sucesso:', res.data);
        return res.data;
      })
      .catch((error: any) => {
        console.error('❌ configApi.syncFromScripts - Erro:', error.response?.data);
        throw error;
      });
  },
};

/**
 * Gmail Integration API
 * Multi-tenant Gmail API integration with OAuth2 authentication
 */
export const gmailApi = {
  /**
   * Obtém a URL de autorização OAuth2 para Gmail
   */
  getOAuthUrl: (state?: string): Promise<ApiResponse<GmailOAuthUrlResponse>> => {
    return api.get('/api/gmail/oauth/authorize', {
      params: state ? { state } : {}
    }).then((res: any) => res.data);
  },

  /**
   * Processa callback OAuth2 (usado internamente)
   */
  handleOAuthCallback: (code: string, state?: string): Promise<ApiResponse<GmailOAuthCallbackResponse>> => {
    return api.get('/api/gmail/callback', {
      params: { code, state }
    }).then((res: any) => res.data);
  },

  /**
   * Lista todas as labels (etiquetas) da conta Gmail
   */
  getLabels: (userId?: string): Promise<ApiResponse<GmailLabelsResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/gmail/labels', { headers }).then((res: any) => res.data);
  },

  /**
   * Lista mensagens de uma label específica
   */
  getMessages: (labelId?: string, maxResults?: number, userId?: string): Promise<ApiResponse<GmailMessagesResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/gmail/messages', {
      params: { labelId, maxResults: maxResults || 50 },
      headers
    }).then((res: any) => res.data);
  },

  /**
   * Obtém detalhes completos de uma mensagem específica
   */
  getMessage: (messageId: string, userId?: string): Promise<ApiResponse<GmailMessage>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get(`/api/gmail/messages/${messageId}`, { headers }).then((res: any) => res.data);
  },

  /**
   * Envia uma mensagem de e-mail
   */
  sendMessage: (message: GmailSendMessageRequest, userId?: string): Promise<ApiResponse<GmailSendMessageResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.post('/api/gmail/messages', message, { headers }).then((res: any) => res.data);
  },

  /**
   * Cria um rascunho de mensagem
   */
  createDraft: (message: GmailSendMessageRequest, userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.post('/api/gmail/drafts', message, { headers }).then((res: any) => res.data);
  },

  /**
   * Modifica labels de uma mensagem
   */
  modifyLabels: (messageId: string, addLabelIds?: string[], removeLabelIds?: string[], userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    const payload = { addLabelIds, removeLabelIds };
    return api.patch(`/api/gmail/messages/${messageId}/labels`, payload, { headers }).then((res: any) => res.data);
  },

  /**
   * Configura token Gmail manualmente para o usuário atual (multi-tenant)
   */
  configureToken: (accessToken: string, refreshToken?: string, email?: string, expiresAt?: number, userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    const payload = { accessToken, refreshToken, email, expiresAt };
    return api.post('/api/gmail/config/token', payload, { headers }).then((res: any) => res.data);
  },

  /**
   * Obtém a configuração de token Gmail do usuário atual
   */
  getTokenConfig: (userId?: string): Promise<ApiResponse<GmailTokenConfigStatus>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/gmail/config/token', { headers }).then((res: any) => res.data);
  },

  /**
   * Remove a configuração de token Gmail do usuário atual
   */
  deleteTokenConfig: (userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.delete('/api/gmail/config/token', { headers }).then((res: any) => res.data);
  },

  /**
   * Health check do serviço Gmail
   */
  health: (): Promise<ApiResponse> => {
    return api.get('/api/gmail/health').then((res: any) => res.data);
  },

  // ==================== Automations ====================

  /**
   * Lista todas as automações Gmail
   */
  listAutomations: (userId?: string): Promise<ApiResponse<{ automations: any[]; total: number }>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/gmail/automations', { headers }).then((res: any) => res.data);
  },

  /**
   * Obtém uma automação específica
   */
  getAutomation: (id: string, userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get(`/api/gmail/automations/${id}`, { headers }).then((res: any) => res.data);
  },

  /**
   * Cria uma nova automação
   */
  createAutomation: (automation: any, userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.post('/api/gmail/automations', automation, { headers }).then((res: any) => res.data);
  },

  /**
   * Atualiza uma automação existente
   */
  updateAutomation: (id: string, automation: any, userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.put(`/api/gmail/automations/${id}`, automation, { headers }).then((res: any) => res.data);
  },

  /**
   * Ativa ou desativa uma automação
   */
  toggleAutomation: (id: string, active: boolean, userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.patch(`/api/gmail/automations/${id}/toggle`, { active }, { headers }).then((res: any) => res.data);
  },

  /**
   * Deleta uma automação
   */
  deleteAutomation: (id: string, userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.delete(`/api/gmail/automations/${id}`, { headers }).then((res: any) => res.data);
  },

  // ==================== Automation History ====================

  /**
   * Lista histórico de execuções de automações
   */
  getAutomationHistory: (userId?: string, status?: string, limit?: number): Promise<ApiResponse<{ 
    history: any[]; 
    total: number;
    stats: {
      totalExecutions: number;
      successCount: number;
      errorCount: number;
    }
  }>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    const params: any = {};
    if (status) params.status = status;
    if (limit) params.limit = limit;
    return api.get('/api/gmail/automations/history', { headers, params }).then((res: any) => res.data);
  },

  /**
   * Lista histórico de uma automação específica
   */
  getAutomationHistoryById: (automationId: string, userId?: string): Promise<ApiResponse<{ 
    history: any[]; 
    total: number;
  }>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get(`/api/gmail/automations/${automationId}/history`, { headers }).then((res: any) => res.data);
  },

  /**
   * Deleta todo histórico do usuário
   */
  deleteHistory: (userId?: string): Promise<ApiResponse<{ deletedCount: number }>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.delete('/api/gmail/automations/history', { headers }).then((res: any) => res.data);
  },
};

/**
 * API para Automações Multi-Canal (Gmail, WhatsApp, Slack)
 */
export const flowsApi = {
  /**
   * Listar fluxos por canal
   */
  listFlows: (channel: string, userId: string): Promise<ApiResponse<any[]>> =>
    api.get(`/api/automations/${channel}`, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Obter fluxo por ID
   */
  getFlow: (channel: string, userId: string, flowId: string): Promise<ApiResponse<any>> =>
    api.get(`/api/automations/${channel}/${flowId}`, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Criar novo fluxo
   */
  createFlow: (channel: string, userId: string, flow: Partial<any>): Promise<ApiResponse<any>> =>
    api.post(`/api/automations/${channel}`, flow, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Atualizar fluxo existente
   */
  updateFlow: (channel: string, userId: string, flowId: string, flow: Partial<any>): Promise<ApiResponse<any>> =>
    api.put(`/api/automations/${channel}/${flowId}`, flow, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Deletar fluxo
   */
  deleteFlow: (channel: string, userId: string, flowId: string): Promise<ApiResponse<void>> =>
    api.delete(`/api/automations/${channel}/${flowId}`, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Executar fluxo manualmente
   */
  executeFlow: (channel: string, userId: string, flowId: string, context?: Record<string, any>): Promise<ApiResponse<{ executionId: string; status: string; logs: any[] }>> =>
    api.post(`/api/automations/${channel}/${flowId}/execute`, context || {}, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Ativar/desativar fluxo
   */
  toggleFlow: (channel: string, userId: string, flowId: string): Promise<ApiResponse<any>> =>
    api.patch(`/api/automations/${channel}/${flowId}/toggle`, {}, {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),

  /**
   * Obter estatísticas de quota (rate limiting)
   */
  getQuotaStats: (userId: string): Promise<ApiResponse<{
    used: number;
    limit: number;
    percentage: number;
    remaining: number;
    alertLevel: 'ok' | 'warning' | 'critical';
  }>> =>
    api.get('/api/automations/quota/stats', {
      headers: { 'X-User-ID': userId }
    }).then(res => res.data),
};

/**
 * API para Integração Braip
 * Gerencia webhooks, configuração e estatísticas de vendas da plataforma Braip
 */
export const braipApi = {
  /**
   * Obtém configuração Braip do usuário
   */
  getConfig: (userId?: string): Promise<ApiResponse<BraipConfigStatus>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/braip/config', { headers }).then((res: any) => {
      console.log('🔍 braipApi.getConfig response:', res.data);
      return res.data;
    });
  },

  /**
   * Salva/atualiza configuração Braip
   */
  saveConfig: (config: BraipConfigRequest, userId?: string): Promise<ApiResponse<any>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.post('/api/braip/config', config, { headers }).then((res: any) => res.data);
  },

  /**
   * Testa validade do token Braip
   */
  testToken: (request: BraipTestTokenRequest, userId?: string): Promise<ApiResponse<BraipTestTokenResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.post('/api/braip/config/test', request, { headers }).then((res: any) => res.data);
  },

  /**
   * Remove configuração Braip
   */
  deleteConfig: (userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.delete('/api/braip/config', { headers }).then((res: any) => res.data);
  },

  /**
   * Obtém histórico de webhooks recebidos
   */
  getWebhookHistory: (userId?: string, limit: number = 50): Promise<ApiResponse<BraipWebhookHistoryResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/braip/webhook-history', {
      headers,
      params: { limit }
    }).then((res: any) => res.data);
  },

  /**
   * Limpa histórico de webhooks
   */
  clearWebhookHistory: (userId?: string): Promise<ApiResponse> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.delete('/api/braip/webhook-history', { headers }).then((res: any) => res.data);
  },

  /**
   * Obtém estatísticas da integração Braip
   */
  getStats: (userId?: string): Promise<ApiResponse<BraipStats>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/braip/stats', { headers }).then((res: any) => res.data);
  },

  /**
   * Obtém lista de produtos Braip (preparado para futuro)
   */
  getProducts: (userId?: string): Promise<ApiResponse<BraipProductsResponse>> => {
    const headers = userId ? { 'X-User-ID': userId } : {};
    return api.get('/api/braip/products', { headers }).then((res: any) => res.data);
  },

  /**
   * Health check da integração Braip
   */
  health: (): Promise<ApiResponse<BraipHealthResponse>> => {
    return api.get('/api/braip/health').then((res: any) => res.data);
  },
};

export default api;
